const MARKER = 'guided-template';

const DETAILS_FIELD_LABELS = {
  service: 'Service',
  date: 'Date',
  time: 'Time',
  oldDate: 'Original date',
  oldTime: 'Original time',
  newDate: 'New date',
  newTime: 'New time',
  depositAmount: 'Deposit paid',
  remainingBalance: 'Remaining balance',
  totalAmount: 'Total amount',
  balanceAmount: 'Balance paid'
};

const DETAIL_FIELDS = Object.keys(DETAILS_FIELD_LABELS);
const SCHEDULE_CHANGE_DETAIL_ITEMS = ['oldDateTime', 'newDateTime', 'service'];
const DEFAULT_THEME_COLOR = '#1976d2';

const escapeHtml = (value = '') =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatMultilineText = (value = '') => escapeHtml(value).replace(/\n/g, '<br />');

const normalizeHexColor = (value) => {
  if (typeof value !== 'string') {
    return DEFAULT_THEME_COLOR;
  }

  const trimmed = value.trim();
  return /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.test(trimmed) ? trimmed : DEFAULT_THEME_COLOR;
};

const hexToRgb = (hex) => {
  const normalized = normalizeHexColor(hex).replace('#', '');
  const expanded = normalized.length === 3
    ? normalized.split('').map((char) => `${char}${char}`).join('')
    : normalized;

  return {
    r: parseInt(expanded.slice(0, 2), 16),
    g: parseInt(expanded.slice(2, 4), 16),
    b: parseInt(expanded.slice(4, 6), 16)
  };
};

const mixColor = (hex, target, amount) => {
  const source = hexToRgb(hex);
  const mixed = {
    r: Math.round(source.r + (target.r - source.r) * amount),
    g: Math.round(source.g + (target.g - source.g) * amount),
    b: Math.round(source.b + (target.b - source.b) * amount)
  };

  return `#${[mixed.r, mixed.g, mixed.b].map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
};

const withAlpha = (hex, alpha) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const createEmailPalette = (themeColor) => {
  const primary = normalizeHexColor(themeColor);

  return {
    primary,
    primaryDark: mixColor(primary, { r: 15, g: 23, b: 42 }, 0.18),
    mutedText: '#475569',
    subtleText: '#64748b',
    buttonShadow: withAlpha(primary, 0.24)
  };
};

const stripHtml = (value = '') => {
  if (!value) {
    return '';
  }

  if (typeof window !== 'undefined' && window.DOMParser) {
    const document = new window.DOMParser().parseFromString(value, 'text/html');
    return (document.body.textContent || '').replace(/\s+/g, ' ').trim();
  }

  return value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
};

const makeMarker = (key, value) => `<!-- ${MARKER}:${key}:${encodeURIComponent(value || '')} -->`;

const readMarker = (body, key) => {
  const match = body.match(new RegExp(`<!-- ${MARKER}:${key}:([^>]*) -->`));
  return match ? decodeURIComponent(match[1]) : '';
};

const getDetailFieldsForTemplate = (mergeFields = []) => mergeFields.filter((field) => DETAIL_FIELDS.includes(field));

const hasPaymentFields = (mergeFields = []) =>
  mergeFields.some((field) => ['depositAmount', 'remainingBalance', 'totalAmount', 'balanceAmount'].includes(field));

const hasScheduleChangeFields = (mergeFields = []) =>
  mergeFields.some((field) => ['oldDate', 'oldTime', 'newDate', 'newTime'].includes(field));

const getDefaultDetailsOrder = (mergeFields = []) => {
  if (hasScheduleChangeFields(mergeFields)) {
    return SCHEDULE_CHANGE_DETAIL_ITEMS.filter((item) => item === 'service' ? mergeFields.includes('service') : true);
  }

  return getDetailFieldsForTemplate(mergeFields);
};

const normalizeDetailsOrder = (detailsOrder = [], mergeFields = []) => {
  const defaultOrder = getDefaultDetailsOrder(mergeFields);
  const allowedItems = new Set(defaultOrder);
  const uniqueRequestedItems = Array.isArray(detailsOrder)
    ? detailsOrder.filter((item, index) => allowedItems.has(item) && detailsOrder.indexOf(item) === index)
    : [];

  return [
    ...uniqueRequestedItems,
    ...defaultOrder.filter((item) => !uniqueRequestedItems.includes(item))
  ];
};

const getDetailsOrderFromMarker = (body = '', mergeFields = []) =>
  normalizeDetailsOrder(
    (readMarker(body, 'detailsOrder') || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean),
    mergeFields
  );

export const getAvailableDetailItems = (mergeFields = []) =>
  normalizeDetailsOrder(getDefaultDetailsOrder(mergeFields), mergeFields).map((item) => {
    if (item === 'oldDateTime') {
      return { id: item, label: 'Original date & time' };
    }

    if (item === 'newDateTime') {
      return { id: item, label: 'New date & time' };
    }

    return {
      id: item,
      label: DETAILS_FIELD_LABELS[item] || item
    };
  });

const getDefaultIntro = (mergeFields = []) => {
  if (hasScheduleChangeFields(mergeFields)) {
    return 'Your appointment has been updated. We wanted to share the latest details with you right away.';
  }

  if (mergeFields.includes('depositAmount')) {
    return 'Thank you for your deposit payment. Here is a quick summary of your appointment and payment details.';
  }

  if (mergeFields.includes('balanceAmount')) {
    return 'Thank you for completing your payment. Here is a quick confirmation for your appointment.';
  }

  if (mergeFields.includes('portalUrl')) {
    return 'We are looking forward to seeing you. Here is a quick summary of your appointment.';
  }

  return 'Here is a quick summary to keep everything in one place.';
};

const getDefaultClosing = (mergeFields = []) => {
  if (mergeFields.includes('portalUrl')) {
    return 'If you need to make any updates, you can use your booking link below or contact us directly.';
  }

  return 'If you have any questions, please contact us using the business details provided.';
};

const getDefaultSignature = (mergeFields = []) => {
  if (mergeFields.includes('balanceAmount')) {
    return 'We truly appreciate your business and look forward to seeing you soon.';
  }

  if (hasScheduleChangeFields(mergeFields)) {
    return 'Thank you for your flexibility and understanding.';
  }

  return 'Thank you for choosing us. We look forward to seeing you soon.';
};

export const getDefaultDetailsHeading = (mergeFields = []) => {
  if (hasScheduleChangeFields(mergeFields)) {
    return 'Updated appointment details';
  }

  if (hasPaymentFields(mergeFields)) {
    return 'Payment details';
  }

  return 'Appointment details';
};

export const createDefaultGuidedTemplate = (mergeFields = []) => ({
  greeting: 'Hi {{customerName}},',
  intro: getDefaultIntro(mergeFields),
  detailsHeading: getDefaultDetailsHeading(mergeFields),
  includeDetailsCard: getDetailFieldsForTemplate(mergeFields).length > 0,
  detailsOrder: getDefaultDetailsOrder(mergeFields),
  closing: getDefaultClosing(mergeFields),
  signature: getDefaultSignature(mergeFields),
  ctaText: mergeFields.includes('portalUrl') ? 'View or manage your booking' : ''
});

const getParagraphsFromBody = (body = '') => {
  if (!body || typeof window === 'undefined' || !window.DOMParser) {
    return [];
  }

  const document = new window.DOMParser().parseFromString(body, 'text/html');
  return Array.from(document.body.querySelectorAll('p'))
    .map((node) => (node.textContent || '').replace(/\s+/g, ' ').trim())
    .filter(Boolean);
};

const getFirstHeadingFromBody = (body = '') => {
  if (!body || typeof window === 'undefined' || !window.DOMParser) {
    return '';
  }

  const document = new window.DOMParser().parseFromString(body, 'text/html');
  const heading = document.body.querySelector('h3, h4');
  return heading ? (heading.textContent || '').replace(/\s+/g, ' ').trim() : '';
};

const getFirstPortalLinkText = (body = '') => {
  if (!body || typeof window === 'undefined' || !window.DOMParser) {
    return '';
  }

  const document = new window.DOMParser().parseFromString(body, 'text/html');
  const portalLink = Array.from(document.body.querySelectorAll('a')).find((anchor) =>
    (anchor.getAttribute('href') || '').includes('{{portalUrl}}')
  );

  return portalLink ? (portalLink.textContent || '').replace(/\s+/g, ' ').trim() : '';
};

export const createGuidedTemplateFromBody = (body = '', mergeFields = []) => {
  const defaults = createDefaultGuidedTemplate(mergeFields);

  if (!body) {
    return defaults;
  }

  if (body.includes(`<!-- ${MARKER}`)) {
    return {
      greeting: readMarker(body, 'greeting') || defaults.greeting,
      intro: readMarker(body, 'intro') || defaults.intro,
      detailsHeading: readMarker(body, 'detailsHeading') || defaults.detailsHeading,
      includeDetailsCard: (readMarker(body, 'includeDetailsCard') || String(defaults.includeDetailsCard)) === 'true',
      detailsOrder: getDetailsOrderFromMarker(body, mergeFields),
      closing: readMarker(body, 'closing') || defaults.closing,
      signature: readMarker(body, 'signature') || defaults.signature,
      ctaText: readMarker(body, 'ctaText') || defaults.ctaText
    };
  }

  const paragraphs = getParagraphsFromBody(body);
  const greeting = paragraphs.find((paragraph) => paragraph.includes('{{customerName}}')) || defaults.greeting;
  const nonGreetingParagraphs = paragraphs.filter((paragraph) => paragraph !== greeting);
  const detailFields = getDetailFieldsForTemplate(mergeFields);
  const detailRegex = detailFields.length
    ? new RegExp(detailFields.map((field) => `{{${field}}}`).join('|'))
    : null;
  const copyParagraphs = nonGreetingParagraphs.filter((paragraph) => !detailRegex || !detailRegex.test(paragraph));

  return {
    greeting,
    intro: copyParagraphs[0] || defaults.intro,
    detailsHeading: getFirstHeadingFromBody(body) || defaults.detailsHeading,
    includeDetailsCard: detailFields.length > 0 && detailFields.some((field) => body.includes(`{{${field}}}`)),
    detailsOrder: defaults.detailsOrder,
    closing: copyParagraphs[1] || defaults.closing,
    signature: copyParagraphs[2] || defaults.signature,
    ctaText: getFirstPortalLinkText(body) || defaults.ctaText
  };
};

export const buildGuidedTemplateBody = (guidedTemplate, mergeFields = [], themeColor) => {
  const detailFields = getDetailFieldsForTemplate(mergeFields);
  const detailsOrder = normalizeDetailsOrder(guidedTemplate.detailsOrder, mergeFields);
  const companyData = typeof themeColor === 'object' && themeColor !== null ? themeColor : { themeColor };
  const palette = createEmailPalette(companyData?.themeColor);
  const showCompanyContact = hasPaymentFields(mergeFields);
  const detailsRows = guidedTemplate.includeDetailsCard
    ? (() => {
        if (hasScheduleChangeFields(mergeFields)) {
          return detailsOrder
            .map((item) => {
              if (item === 'oldDateTime') {
                return `<p style="margin: 0 0 12px; font-size: 14px; line-height: 1.7; color: #0f172a;"><span style="font-weight: 700; color: ${palette.primaryDark};">Original date & time:</span> {{oldDate}} {{oldTime}}</p>`;
              }

              if (item === 'newDateTime') {
                return `<p style="margin: 0 0 12px; font-size: 14px; line-height: 1.7; color: #0f172a;"><span style="font-weight: 700; color: ${palette.primaryDark};">New date & time:</span> {{newDate}} {{newTime}}</p>`;
              }

              if (item === 'service' && mergeFields.includes('service')) {
                return `<p style="margin: 0 0 12px; font-size: 14px; line-height: 1.7; color: #0f172a;"><span style="font-weight: 700; color: ${palette.primaryDark};">Service:</span> {{service}}</p>`;
              }

              return '';
            })
            .filter(Boolean)
            .join('\n');
        }

        return (detailsOrder.length > 0 ? detailsOrder : detailFields)
          .map((field) => {
            const label = DETAILS_FIELD_LABELS[field];
            if (!label) {
              return '';
            }

            return `<p style="margin: 0 0 12px; font-size: 14px; line-height: 1.7; color: #0f172a;"><span style="font-weight: 700; color: ${palette.primaryDark};">${label}:</span> {{${field}}}</p>`;
          })
          .filter(Boolean)
          .join('\n');
      })()
    : '';

  const contactHtml = showCompanyContact && (companyData?.phone || companyData?.email)
    ? `
      <div style="margin: 24px 0 0;">
        ${companyData?.phone ? `<p style="margin: 0 0 8px; font-size: 14px; line-height: 1.7; color: ${palette.mutedText};"><strong style="color: #0f172a;">Phone:</strong> ${escapeHtml(companyData.phone)}</p>` : ''}
        ${companyData?.email ? `<p style="margin: 0; font-size: 14px; line-height: 1.7; color: ${palette.mutedText};"><strong style="color: #0f172a;">Email:</strong> ${escapeHtml(companyData.email)}</p>` : ''}
      </div>
    `
    : '';

  const ctaHtml =
    guidedTemplate.ctaText && mergeFields.includes('portalUrl')
      ? `
      <div style="margin: 28px 0 0;">
        <a href="{{portalUrl}}" style="display: inline-block; padding: 12px 18px; border-radius: 999px; background: ${palette.primary}; color: #ffffff; text-decoration: none; font-weight: 700; box-shadow: 0 8px 18px ${palette.buttonShadow};">
          ${formatMultilineText(guidedTemplate.ctaText)}
        </a>
      </div>
      `
      : '';

  return `
    ${makeMarker('greeting', guidedTemplate.greeting)}
    ${makeMarker('intro', guidedTemplate.intro)}
    ${makeMarker('detailsHeading', guidedTemplate.detailsHeading)}
    ${makeMarker('includeDetailsCard', String(guidedTemplate.includeDetailsCard))}
    ${makeMarker('detailsOrder', detailsOrder.join(','))}
    ${makeMarker('closing', guidedTemplate.closing)}
    ${makeMarker('signature', guidedTemplate.signature)}
    ${makeMarker('ctaText', guidedTemplate.ctaText)}
    <div style="font-family: Arial, sans-serif; max-width: 620px; margin: 0; color: #0f172a; text-align: left;">
      <p style="margin: 0 0 14px; font-size: 16px; line-height: 1.7; color: #0f172a;">${formatMultilineText(guidedTemplate.greeting)}</p>
      <p style="margin: 0 0 22px; font-size: 15px; line-height: 1.75; color: ${palette.mutedText};">${formatMultilineText(guidedTemplate.intro)}</p>
      ${
        guidedTemplate.includeDetailsCard && detailsRows
          ? `
        <h3 style="margin: 0 0 14px; color: ${palette.primary}; font-size: 16px; font-weight: 700;">${formatMultilineText(guidedTemplate.detailsHeading)}</h3>
        <div style="margin: 0 0 24px;">
          ${detailsRows}
        </div>
      `
          : ''
      }
      <p style="margin: 0 0 14px; font-size: 15px; line-height: 1.75; color: ${palette.mutedText};">${formatMultilineText(guidedTemplate.closing)}</p>
      <p style="margin: 0; font-size: 15px; line-height: 1.75; color: #0f172a;">${formatMultilineText(guidedTemplate.signature)}</p>
      ${contactHtml}
      ${ctaHtml}
      <p style="margin: 26px 0 0; font-size: 13px; line-height: 1.7; color: ${palette.subtleText};">
        We are sharing this so you have the latest appointment details in one convenient place.
      </p>
    </div>
  `;
};

export const createPlainTextPreview = (body = '') => stripHtml(body);
